// src/Routes/Prompts/PromptsList.tsx
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import { GoogleAnalyticsContext } from "../../Hooks/GoogleAnalyticsContext";
import { useAuthenticatedFetch } from "../../Hooks/useFetch";
import config from "../../config";
import PromptList from "../../Components/HighLevelComponents/UserPromptManagement/components/PromptList";
import { Prompt } from "../../Components/HighLevelComponents/UserPromptManagement/components/types";
import SidebarLayout from "../../Components/Layout/SidebarLayout";

const PromptsList: React.FC = () => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { logEvent } = useContext(GoogleAnalyticsContext);

  const { res: promptsRes, callAPI: fetchPrompts } = useAuthenticatedFetch({
    url: `${config.API_URI}/api/prompt`,
    method: "GET",
  });

  const {
    res: deleteRes,
    callAPI: deletePrompt,
    isLoading: isDeleting,
  } = useAuthenticatedFetch({
    url: `${config.API_URI}/api/prompt`,
    method: "DELETE",
  });

  useEffect(() => {
    fetchPrompts();
  }, []);

  useEffect(() => {
    if (promptsRes.statusCode === 200 && promptsRes.data?.prompts) {
      setPrompts(promptsRes.data.prompts);
      setIsLoading(false);
      logEvent("ToolsList", "View", `count_${promptsRes.data.prompts.length}`);
    }
  }, [promptsRes]);

  useEffect(() => {
    if (deleteRes.statusCode === 200) {
      fetchPrompts();
      logEvent("CustomToolAction", "DeleteSuccess");
    }
  }, [deleteRes]);

  const handleNew = () => {
    navigate("/custom-tools/create");
    logEvent("CustomToolAction", "NewTool");
  };

  const handleEdit = (prompt: Prompt) => {
    navigate(`/custom-tools/${prompt.id}/edit`);
    logEvent("CustomToolAction", "EditTool", `tool_${prompt.id}`);
  };

  const handleDelete = (prompt: Prompt) => {
    if (window.confirm(`Are you sure you want to delete "${prompt.title}"?`)) {
      deletePrompt({
        url: `${config.API_URI}/api/prompt/${prompt.id}`,
      });
      logEvent("CustomToolAction", "DeleteTool", `tool_${prompt.id}`);
    }
  };

  const handleUse = (prompt: Prompt) => {
    navigate(`/custom-tools/${prompt.id}`);
    logEvent("CustomToolAction", "UseTool", `tool_${prompt.id}`);
  };

  return (
    <SidebarLayout>
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              My Custom Tools
            </h1>
            <p className="text-gray-600 mt-1">
              Create and manage your own custom tools to automate repetitive
              tasks
            </p>
          </div>
          <button
            onClick={handleNew}
            className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-800 rounded-lg flex items-center transition-colors"
          >
            <FaPlus className="mr-2" /> Create New Tool
          </button>
        </div>

        <PromptList
          prompts={prompts}
          isLoading={isLoading}
          isDeleting={isDeleting}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onUse={handleUse}
        />
      </div>
    </SidebarLayout>
  );
};

export default PromptsList;
